"use client";

import {
  Suspense,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  use,
} from "react";
import { notFound, useRouter } from "next/navigation";
import { supabase } from "@/utils/supabase/client";
import useStore from "@/store/useStore";
import UnderConstruction from "@/components/space/UnderConstruction";
import ClaimSpace from "@/components/space/ClaimSpace";
import PasswordProtection from "@/components/space/PasswordProtection";
import MasonryLayout from "@/components/space/MasonryLayout";
import { useResponsiveLayout, BREAKPOINTS } from "@/hooks/useResponsiveLayout";
import { useImmediateLayoutFix } from "@/components/space/LayoutFixer";
import { useHotkeys } from "react-hotkeys-hook";

// Import extracted components
import LoadingSpace from "@/components/space/LoadingSpace";
import StructuredData from "@/components/space/StructuredData";

// Import utility functions
import {
  isLightBackground,
  getHeaderButtonStyles,
  renderBackgroundMedia,
  getPageStyles,
  getThemeStyles,
  calculateAverageColor,
} from "@/utils/themeUtils";

import SpaceHeader from "@/components/space/SpaceHeader";
import SubscriptionReactivationNeeded from "@/components/space/SubscriptionReactivationNeeded";
import { navigationEvents, spaceEvents } from "@/utils/analytics";

// Cache configuration
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

// Helper functions to check space data status
const isSpaceNotFound = (data) => {
  return data?.notFound || data?.status === 404;
};

const isSpaceUnderConstruction = (data) => {
  return (
    data?.underConstruction ||
    (data?.status === 403 && data?.error === "Space is under construction")
  );
};

const isPasswordRequired = (data) => {
  return data?.passwordRequired || data?.status === 401;
};

const isHandleLocked = (data) => {
  return data?.space_data?.status === 425 && data?.error === "Handle locked";
};

const isHandleInDemand = (data) => {
  return data?.space_data?.status === 423 && data?.error === "Handle in demand";
};

const isMissedOpportunity = (data) => {
  return data?.status === 410 && data?.error === "Missed opportunity";
};

const isDisallowedSlug = (data) => {
  return data?.status === 403 && data?.error === "Slug not allowed";
};

const isSubscriptionReactivationRequired = (data) => {
  return (
    data?.status === 422 && data?.error === "Subscription reactivation required"
  );
};

export default function SpacePage({ params }) {
  // Properly unwrap params using React.use()
  const resolvedParams = use(params);
  const spaceSlug = resolvedParams.space;

  // Store hooks
  const session = useStore((state) => state.session);
  const getSpaceContent = useStore((state) => state.getSpaceContent);
  const doesSpaceNeedRefresh = useStore((state) => state.doesSpaceNeedRefresh);
  const cacheSpaceContent = useStore((state) => state.cacheSpaceContent);
  const router = useRouter();

  // State hooks
  const [spaceData, setSpaceData] = useState(null);
  const [waitingForSession, setWaitingForSession] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [averageBackgroundColor, setAverageBackgroundColor] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [hasSessionLoaded, setHasSessionLoaded] = useState(false);
  const [tabBecameVisible, setTabBecameVisible] = useState(false);
  const [hasEverHadSession, setHasEverHadSession] = useState(false);
  const [widgetsFlyOff, setWidgetsFlyOff] = useState(false);

  // Refs for tracking state
  const prevSessionRef = useRef(null);
  const shouldFetchRef = useRef(true);
  const spaceDataFetchedRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  const lastFetchTimeRef = useRef(null);
  const masonryRef = useRef(null);
  const sessionCheckTimeoutRef = useRef(null);
  const visibilityCheckCountRef = useRef(0);

  // Use the responsive layout hook
  const { activeLayout, windowWidth } = useResponsiveLayout();

  const isMobileDevice = useMemo(() => {
    if (typeof window === "undefined") return false;
    return windowWidth <= BREAKPOINTS.tablet;
  }, [windowWidth]);

  // Check URL parameters to see if public=true is explicitly requested
  // This is useful for iframe previews and other embedded views
  const isPublicView = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("public") === "true";
  }, []);

  // Check if this is a screenshot generation request
  const isScreenshotMode = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("screenshot") === "true";
  }, []);

  // Check other screenshot optimization parameters
  const shouldHideUI = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("hideUI") === "true";
  }, []);

  const shouldDisableAnimations = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("noAnimations") === "true";
  }, []);

  // Add visibility change handler to handle tab switching
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        visibilityCheckCountRef.current += 1;
        console.log(
          `🔄 Tab became visible (check #${visibilityCheckCountRef.current}), checking session state`
        );
        setTabBecameVisible(true);

        // Immediately check if we have a session from the store
        const currentSession = useStore.getState().session;
        console.log(
          `📊 Current session state from store: ${currentSession === undefined ? "undefined" : currentSession ? "authenticated" : "null"}`
        );

        // If we're stuck waiting for session, force a check
        if (waitingForSession && !hasSessionLoaded) {
          console.log("⚡ Forcing session check after tab visibility");

          // Clear any existing timeout
          if (sessionCheckTimeoutRef.current) {
            clearTimeout(sessionCheckTimeoutRef.current);
          }

          // If we've ever had a session or the current session is not undefined, immediately stop waiting
          if (hasEverHadSession || currentSession !== undefined) {
            console.log(
              "✅ Have session history or current session, immediately stopping wait"
            );
            setWaitingForSession(false);
            setHasSessionLoaded(true);
          } else {
            // Give a very brief moment for session to stabilize if truly needed
            sessionCheckTimeoutRef.current = setTimeout(() => {
              console.log(
                "⏳ Forcing load state reset after visibility timeout"
              );
              setWaitingForSession(false);
              setHasSessionLoaded(true);
            }, 100); // Reduced to 100ms for faster recovery
          }
        }

        // If session is already loaded but we're still somehow waiting, fix it
        if (hasSessionLoaded && waitingForSession) {
          console.warn(
            "⚠️ Inconsistent state detected: hasSessionLoaded=true but waitingForSession=true, fixing..."
          );
          setWaitingForSession(false);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Also check immediately on mount in case tab is already visible
    if (
      document.visibilityState === "visible" &&
      waitingForSession &&
      !hasSessionLoaded
    ) {
      handleVisibilityChange();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (sessionCheckTimeoutRef.current) {
        clearTimeout(sessionCheckTimeoutRef.current);
      }
    };
  }, [waitingForSession, hasSessionLoaded, hasEverHadSession]);

  // Add safety timeout to force loading state reset if stuck
  useEffect(() => {
    if (waitingForSession && !hasSessionLoaded) {
      console.log("📊 Setting up loading timeout safety check");
      // Set a timeout to force loading state to false if it's stuck in "waiting for session"
      const loadingTimeout = setTimeout(() => {
        console.warn("⚠️ Space page forced loading reset due to timeout");
        setWaitingForSession(false);
        setHasSessionLoaded(true);
      }, 500); // Reduced to 0.5 seconds for much faster recovery

      return () => {
        console.log("🧹 Clearing loading timeout safety check");
        clearTimeout(loadingTimeout);
      };
    }
  }, [waitingForSession, hasSessionLoaded]);

  // isLightBackground function with parameters
  const isBackgroundLight = useCallback(
    () => isLightBackground(spaceData, averageBackgroundColor),
    [spaceData, averageBackgroundColor]
  );

  // Get styles with parameters
  const headerButtonStyles = useMemo(
    () => getHeaderButtonStyles(isBackgroundLight),
    [isBackgroundLight]
  );

  // Create page styles with background
  const pageStyles = useMemo(
    () => getPageStyles(spaceData, isOwner, false),
    [spaceData, isOwner]
  );

  // Apply theme styles from the new schema
  const themeStyles = useMemo(() => getThemeStyles(spaceData), [spaceData]);

  // Use the calculateAverageColor utility function
  useEffect(() => {
    if (typeof window === "undefined") return;

    const background = spaceData?.space_data?.theme?.background;
    if (background?.type === "image" && background.media) {
      // Safely get media URL for color calculation
      const mediaUrl =
        typeof background.media === "string"
          ? background.media
          : background.media?.url || null;

      if (mediaUrl) {
        calculateAverageColor(mediaUrl, setAverageBackgroundColor);
      }
    } else {
      setAverageBackgroundColor(null);
    }
  }, [spaceData?.space_data?.theme?.background]);

  // Effect to handle session state changes with debouncing
  useEffect(() => {
    const { debounce } = require("@/utils/debounce");

    // Create a debounced handler for this specific effect
    const handleSessionChange = debounce((newSession) => {
      console.log(
        `🔑 Session state changed: ${newSession === undefined ? "undefined" : newSession ? "authenticated" : "unauthenticated"}`
      );

      // Track if we've ever had a valid session
      if (newSession !== undefined && newSession !== null) {
        setHasEverHadSession(true);
      }

      if (newSession !== undefined || tabBecameVisible || hasEverHadSession) {
        console.log(
          `✅ Session is determined, tab became visible, or have session history - no longer waiting`
        );
        setWaitingForSession(false);
        setHasSessionLoaded(true);
      }
    }, 300); // 300ms debounce

    // Call the debounced handler
    handleSessionChange(session);

    // Cleanup on unmount
    return () => {
      handleSessionChange.cancel();
    };
  }, [session, tabBecameVisible, hasEverHadSession]);

  // Track space view when data is loaded
  useEffect(() => {
    if (
      spaceData?.space_data?.id &&
      spaceData?.space_data?.slug &&
      !waitingForSession
    ) {
      // Track page view
      navigationEvents.pageView(
        "space_view",
        {
          space_slug: spaceData.space_data.slug,
          is_owner: isOwner,
          is_published: spaceData.space_data.status === "published",
        },
        session
      );

      // Track space view event
      spaceEvents.spaceView(
        spaceData.space_data.id,
        spaceData.space_data.slug,
        {
          is_owner: isOwner,
          widget_count: spaceData.widgets?.length || 0,
          theme_type: spaceData.space_data.theme?.type || "default",
        },
        session
      );
    }
  }, [spaceData, isOwner, session, waitingForSession]);

  // Force session check on mount to prevent waiting too long
  useEffect(() => {
    const immediateSessionCheck = () => {
      if (typeof window !== "undefined") {
        const currentSession = useStore.getState().session;
        if (currentSession !== undefined || hasEverHadSession) {
          setWaitingForSession(false);
          setHasSessionLoaded(true);
        }
      }
    };

    // Check immediately and also after a brief delay
    immediateSessionCheck();
    const quickTimeout = setTimeout(immediateSessionCheck, 50);

    return () => clearTimeout(quickTimeout);
  }, [hasEverHadSession]);

  // Check if user is owner when session and space data are available
  useEffect(() => {
    const checkOwnership = async () => {
      if (!session?.user?.id || !spaceData?.space_data?.id) {
        setIsOwner(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("spaces")
          .select("user_id")
          .eq("id", spaceData.space_data.id)
          .single();

        if (!error && data) {
          setIsOwner(data.user_id === session.user.id);
          console.log(
            `👤 Ownership check: ${data.user_id === session.user.id ? "Owner" : "Not owner"}`
          );
        }
      } catch (err) {
        console.error("Error checking ownership:", err);
        setIsOwner(false);
      }
    };

    checkOwnership();
  }, [session?.user?.id, spaceData?.space_data?.id]);

  // Optimized getSpaceData with enhanced caching - only gets public data
  const getSpaceData = useCallback(
    async (isMounted = true) => {
      if (!isMounted) return;

      console.log(`🌐 Getting public space data for slug: ${spaceSlug}`);

      try {
        if (fetchInProgressRef.current) {
          console.log("⚠️ Fetch already in progress, will not start another");
          return;
        }

        fetchInProgressRef.current = true;
        setIsContentLoading(true);

        // Use cached space data fetch for better performance
        const { getCachedSpaceData } = await import("@/utils/supabase/cache");

        const data = await getCachedSpaceData(spaceSlug, async () => {
          // Fetch directly from supabase using public endpoint
          const { data, error } = await supabase.rpc(
            "get_space_content_public",
            {
              p_slug: spaceSlug,
            }
          );

          if (error) throw error;
          return data;
        });

        if (!isMounted) return;

        // Check for special cases
        if (isSpaceNotFound(data)) {
          setSpaceData({
            notFound: true,
            spaceSlug,
            error: data.error,
            status: data.status || 404,
          });
          setIsContentLoading(false);
          setIsInitialLoading(false);
          fetchInProgressRef.current = false;
          return;
        }

        if (isSpaceUnderConstruction(data)) {
          setSpaceData({
            underConstruction: true,
            spaceSlug,
            error: data.error,
            status: data.status || 403,
          });
          setIsContentLoading(false);
          setIsInitialLoading(false);
          fetchInProgressRef.current = false;
          return;
        }

        if (isPasswordRequired(data)) {
          setSpaceData({
            passwordRequired: true,
            spaceSlug,
            status: data.status || 401,
          });
          setIsContentLoading(false);
          setIsInitialLoading(false);
          fetchInProgressRef.current = false;
          return;
        }

        if (
          isHandleLocked(data) ||
          isHandleInDemand(data) ||
          isMissedOpportunity(data) ||
          isDisallowedSlug(data)
        ) {
          // Pass the data directly to set all error information
          setSpaceData(data);
          setIsContentLoading(false);
          setIsInitialLoading(false);
          fetchInProgressRef.current = false;
          return;
        }

        // Set the data and update states
        setSpaceData(data);
        cacheSpaceContent(
          isPublicView ? `${spaceSlug}_public` : spaceSlug,
          data
        );
        lastFetchTimeRef.current = Date.now();
        setIsContentLoading(false);
        setIsInitialLoading(false);
        spaceDataFetchedRef.current = true;
      } catch (error) {
        console.error("❌ Error in getSpaceData:", error);
        if (isMounted) {
          setSpaceData({
            notFound: true,
            spaceSlug,
            error: "Space not found",
            status: 404,
          });
          setIsContentLoading(false);
          setIsInitialLoading(false);
          fetchInProgressRef.current = false;
        }
      } finally {
        fetchInProgressRef.current = false;
      }
    },
    [
      spaceSlug,
      getSpaceContent,
      cacheSpaceContent,
      doesSpaceNeedRefresh,
      isPublicView,
    ]
  );

  // Effect to update document title
  useEffect(() => {
    if (spaceData?.space_data?.title) {
      document.title = `${spaceData.space_data.title}`;
    } else {
      document.title = "Qensa";
    }

    return () => {
      document.title = "Qensa";
    };
  }, [spaceData?.space_data?.title]);

  // Effect to fetch space data once when page loads
  useEffect(() => {
    let isMounted = true;
    getSpaceData(isMounted);

    // Set up a periodic refresh to keep data fresh
    const refreshInterval = setInterval(() => {
      if (doesSpaceNeedRefresh(spaceSlug)) {
        console.log("🔄 Refreshing space data due to modification flag");
        getSpaceData(isMounted);
      }
    }, 30000); // Check every 30 seconds if refresh is needed

    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
    };
  }, [getSpaceData, spaceSlug, doesSpaceNeedRefresh]);

  // Optimize masonry recalculation
  useEffect(() => {
    if (
      !masonryRef.current?.recalculateLayout ||
      isInitialLoading ||
      !spaceData
    )
      return;

    const recalculateTimeout = setTimeout(() => {
      masonryRef.current.recalculateLayout();
    }, 500);

    return () => clearTimeout(recalculateTimeout);
  }, [spaceData, isInitialLoading]);

  // Add Shift+C hotkey for widget fly-off animation
  useHotkeys("shift+c", (e) => {
    e.preventDefault();
    if (!spaceData?.widgets?.length) return;

    // Toggle the fly-off state
    setWidgetsFlyOff((prev) => !prev);
  });

  // Add Shift+S hotkey for share functionality
  useHotkeys("shift+s", (e) => {
    e.preventDefault();

    // Find the share button in the SpaceHeader component
    const shareButton = document.querySelector(
      'button[aria-label="Share space"]'
    );

    if (shareButton) {
      // Check if the button is visible (not hidden by display styles)
      const isVisible =
        window.getComputedStyle(shareButton).display !== "none" &&
        window.getComputedStyle(shareButton).visibility !== "hidden" &&
        shareButton.offsetWidth > 0 &&
        shareButton.offsetHeight > 0;

      if (isVisible) {
        shareButton.click();
      }
    }
  });

  // Apply immediate layout fixes for viewing mode
  const layoutFixedWidgets = useImmediateLayoutFix(
    spaceData?.widgets,
    activeLayout,
    !isOwner // Enable fixes for non-owners (viewing mode)
  );

  // Add dummy widget update function to prevent errors in public view
  const handleWidgetUpdate = useCallback((widgetId, changes) => {
    console.log(
      "Widget update attempted in public view (no-op):",
      widgetId,
      changes
    );
    // This is a no-op function in public view
    return false;
  }, []);

  // Optimize renderedWidgets using useMemo
  const renderedWidgets = useMemo(() => {
    return (
      <MasonryLayout
        ref={masonryRef}
        widgets={layoutFixedWidgets || []}
        isEditing={false}
        spaceId={spaceData?.space_data?.id}
        spaceSlug={spaceSlug}
        spaceData={spaceData}
        isLightBackground={isBackgroundLight}
        handleWidgetUpdate={handleWidgetUpdate}
        widgetsFlyOff={widgetsFlyOff}
      />
    );
  }, [
    spaceData,
    spaceSlug,
    masonryRef,
    isBackgroundLight,
    handleWidgetUpdate,
    layoutFixedWidgets,
    widgetsFlyOff,
  ]);

  // Loading states - improved logic
  if (
    waitingForSession &&
    !hasSessionLoaded &&
    !tabBecameVisible &&
    !hasEverHadSession
  ) {
    // Only show loading if we're truly waiting for initial session load
    // and haven't had any indication that we should proceed
    console.log("🔄 Showing loading screen - waiting for session");
    return <LoadingSpace />;
  }

  // Handle error cases before attempting to access space_data
  if (isSpaceNotFound(spaceData)) {
    return <ClaimSpace spaceSlug={spaceSlug} error={spaceData} />;
  }

  if (isSubscriptionReactivationRequired(spaceData)) {
    return (
      <SubscriptionReactivationNeeded
        spaceSlug={spaceSlug}
        error={spaceData}
        isOwner={isOwner}
      />
    );
  }

  if (
    isHandleInDemand(spaceData) ||
    isHandleLocked(spaceData) ||
    isMissedOpportunity(spaceData) ||
    isDisallowedSlug(spaceData)
  ) {
    return <ClaimSpace spaceSlug={spaceSlug} error={spaceData} />;
  }

  if (isSpaceUnderConstruction(spaceData)) {
    return <UnderConstruction spaceSlug={spaceSlug} error={spaceData} />;
  }

  if (!spaceData?.space_data) {
    if (isContentLoading || isInitialLoading) {
      return <LoadingSpace />;
    }
    // If no space_data and not loading, something went wrong - show 404
    return (
      <ClaimSpace
        spaceSlug={spaceSlug}
        error={{ status: 404, error: "Space not found" }}
      />
    );
  }

  if (
    spaceData?.space_data?.require_password &&
    !isOwner &&
    !isPasswordVerified
  ) {
    return (
      <PasswordProtection
        spaceId={spaceData.space_data.id}
        onPasswordVerified={() => setIsPasswordVerified(true)}
      />
    );
  }

  return (
    <div
      style={pageStyles}
      className={`flex flex-col min-h-screen overflow-hidden ${
        shouldDisableAnimations ? "disable-animations" : ""
      }`}
    >
      {/* Add structured data for SEO */}
      <StructuredData
        space={spaceData?.space_data}
        widgets={spaceData?.widgets}
        tags={spaceData?.tags}
      />

      {/* Add global CSS for screenshot optimization */}
      {isScreenshotMode && (
        <style jsx global>{`
          .disable-animations *,
          .disable-animations *::before,
          .disable-animations *::after {
            animation-duration: 0s !important;
            animation-delay: 0s !important;
            transition-duration: 0s !important;
            transition-delay: 0s !important;
          }
        `}</style>
      )}

      {renderBackgroundMedia(spaceData, isOwner, false)}

      {!shouldHideUI && (
        <SpaceHeader
          isLightBackground={isBackgroundLight}
          headerButtonStyles={headerButtonStyles}
          spaceData={spaceData}
          isEditMode={false}
          toggleEditMode={() => router.push(`/${spaceSlug}/edit`)}
          isOwnerOverride={isOwner}
        />
      )}

      <main className="flex-1 overflow-y-auto w-full">
        <div className="container mx-auto py-8 px-4">
          <div
            className="w-full mx-auto transition-all duration-300 flex justify-center"
            style={{
              ...themeStyles,
              background: "transparent",
              boxShadow: "none",
              border: "none",
            }}
          >
            <Suspense fallback={<LoadingSpace />}>{renderedWidgets}</Suspense>
          </div>
        </div>
      </main>
    </div>
  );
}
